import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, Play, ShieldAlert, Clock, Globe } from 'lucide-react';
import { HERO_STATS } from '../mock';
import { useCountry, useCtaHandler } from '../context/CountryContext';

const fadeUp = {
  initial: { filter: 'blur(10px)', opacity: 0, y: 20 },
  animate: { filter: 'blur(0px)', opacity: 1, y: 0 },
};

// Hero uses Inter Black - Title Case - to match the reference visual
const heroHeadingClass =
  'font-hero text-white text-[38px] sm:text-6xl md:text-7xl lg:text-[86px] leading-[1.04] tracking-[-0.03em] px-2';

export default function Hero() {
  const { telegramUrl } = useCountry();
  const track = useCtaHandler();
  return (
    <section id="top" className="relative pt-28 pb-16 sm:pt-36 sm:pb-24 md:pt-44 md:pb-28">
      <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
      <div className="absolute inset-x-0 -top-10 h-[520px] radial-glow pulse-glow pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-6 text-center">
        {/* Headline (Title Case, Inter-based) */}
        <motion.h1
          variants={fadeUp}
          initial="initial"
          animate="animate"
          transition={{ duration: 0.9, ease: 'easeOut', delay: 0.15 }}
          className={heroHeadingClass}
        >
          Trade <span className="text-orange-gradient">Silver.</span>
          <br />
          Hit Your Targets.
          <br />
          Fly To <span className="text-orange-gradient">Bali.</span>
        </motion.h1>

        {/* Sub */}
        <motion.p
          variants={fadeUp}
          initial="initial"
          animate="animate"
          transition={{ duration: 0.8, ease: 'easeOut', delay: 0.55 }}
          className="mt-8 max-w-2xl mx-auto text-[#f2f2f2] text-[15.5px] md:text-base leading-relaxed font-hero-body"
        >
          Welcome to the MBE Road To Bali Challenge. 100% performance based. No lucky draws.
          No random raffles. Trade Silver consistently, unlock up to{' '}
          <span className="text-white font-semibold">USD 200 in cash bonuses</span>, and secure your free luxury trip to Bali.
        </motion.p>

        {/* CTAs */}
        <motion.div
          variants={fadeUp}
          initial="initial"
          animate="animate"
          transition={{ duration: 0.7, ease: 'easeOut', delay: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <a href={telegramUrl} target="_blank" rel="noreferrer" onClick={() => track('hero-primary')} className="liquid-glass-orange rounded-full inline-flex items-center gap-2 px-6 py-3 text-[13.5px]">
            <span className="relative z-[1]">Start Challenge on Telegram</span>
            <ArrowUpRight className="w-4 h-4 relative z-[1]" />
          </a>
          <a href="#system" className="inline-flex items-center gap-2 text-white/90 hover:text-white text-[14px] group uppercase tracking-wide">
            <span className="liquid-glass rounded-full w-9 h-9 flex items-center justify-center">
              <Play className="w-3.5 h-3.5 fill-white text-white relative z-[1]" />
            </span>
            See How It Works
          </a>
        </motion.div>

        <motion.div
          variants={fadeUp}
          initial="initial"
          animate="animate"
          transition={{ duration: 0.6, ease: 'easeOut', delay: 0.95 }}
          className="mt-6 flex items-center justify-center gap-2 text-[11.5px] text-[#f2f2f2] uppercase tracking-wider"
        >
          <ShieldAlert className="w-3.5 h-3.5" />
          Risk Warning: Trading involves risk to your capital. T&Cs apply.
        </motion.div>

        {/* Stats — liquid glass cards */}
        <motion.div
          variants={fadeUp}
          initial="initial"
          animate="animate"
          transition={{ duration: 0.8, ease: 'easeOut', delay: 1.1 }}
          className="mt-14 flex flex-col sm:flex-row items-stretch justify-center gap-5 max-w-3xl mx-auto"
        >
          {HERO_STATS.map((s, i) => (
            <div key={s.label} className="liquid-glass rounded-[1.4rem] p-6 sm:w-[280px] text-left">
              <div className="relative z-[1]">
                {i === 0 ? <Clock className="w-7 h-7 text-white/90" /> : <Globe className="w-7 h-7 text-white/90" />}
                <div className="mt-4 font-display text-4xl md:text-5xl text-orange-gradient leading-none">{s.value}</div>
                <div className="mt-3 text-[#f2f2f2] text-[13px] uppercase tracking-wide">{s.label}</div>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
